import { useEffect, useRef } from 'react';
import { BroadcastChannel } from 'broadcast-channel';

import type { OKAuthConfig as SocialConfig } from 'src/components/AuthNew/utils/socialNetworkUtils';

const createAuthOKUrl = (socialConfig: SocialConfig) => {
  const params = [
    'response_type=token',
    `scope=${socialConfig.scope}`,
    `client_id=${socialConfig.id}`,
    'display=popup',
    `redirect_uri=${window.location.origin}/login/ok`,
  ].join('&');
  return ['https://connect.ok.ru/oauth/authorize', params].join('?');
};

type AuthOKCodeError = {
  success: false;
  error: {
    error?: string;
    error_reason?: string;
    error_description?: string;
  };
};
type AuthOKCodeMessageSuccess = {
  success: true;
  access_token: string;
  session_secret_key: string;
  expires_in: number;
};
type AuthOKCodeMessageResult = AuthOKCodeError | AuthOKCodeMessageSuccess;

type OnSuccess = (payload: AuthOKCodeMessageResult) => void;
type OnError = () => void;

/**
 * @desc процесс авторизации следующий:
 * 1. в новом окне открывается ссылка для авторизации на стороне OK
 * 2. создается Broadcast канал для обмена сообщениями между окном инициатором авторизации
 * и окном, где происходит запрос кода OK
 * 3. после подтверждения доступа, OK перенаправляет на redirect_url из ссылки и добавляет code или ошибки в параметры
 * 4. на странице шаблона (app/views/social_login/OK.haml) скрипт считывает code или ошибки и отправляет их по Broadcast каналу
 * 5. хук передает полученные данные вызываемому методу.
 * */
export const useOKAuth = () => {
  /** ссылка для авторизации в OK */
  /** полученный от OK код авторизации */
  const authCodeRef = useRef<AuthOKCodeMessageResult | undefined>();
  const authChanel = useRef<BroadcastChannel>();

  const resetAuthCodeRef = (): void => {
    authCodeRef.current = undefined;
  };

  /** открыть окно для авторизации через OK */
  const openOKAuthPopup = (url: string): Window | null =>
    window.open(url, '_blank', 'popup');

  /** прослушивание закрытия окна авторизации OK */
  const listenCloseOKAuthPopup =
    (onError: OnError) =>
    (instance: Window | null): void => {
      if (!instance) {
        return;
      }
      const intID = setInterval(() => {
        if (instance?.closed) {
          clearInterval(intID);
          if (!authCodeRef.current || !authCodeRef.current?.success) {
            onError();
          }
        }
      }, 500);
    };

  /** обработка ответа из результирующего окна авторизации через OK */
  const listenOKAuthChanel =
    (onSuccess: OnSuccess, onError: OnError) =>
    (event: AuthOKCodeMessageResult) => {
      if (!event?.success) {
        // eslint-disable-next-line no-console
        console.error('OK AUTH  NOT SUCCESS: ', event);
        onError();
        return;
      }
      authCodeRef.current = event;
      onSuccess(event);
    };

  /**
   * Открыть канал для общения между окном инициатором авторизации
   * и окном в котором происходит получение кода от OK
   * */
  const openOKAuthChanel = (listener) => {
    const chanel = new BroadcastChannel('auth-ok-code');
    authChanel.current = chanel;
    chanel.addEventListener('message', listener);
  };

  const closeOKAuthChanel = () => {
    authChanel.current?.close();
  };

  const handleOKLogin = (
    socialConfig: SocialConfig,
    onSuccess: OnSuccess,
    onError: OnError,
  ): void => {
    try {
      closeOKAuthChanel();
      resetAuthCodeRef();

      const authURL = createAuthOKUrl(socialConfig);
      listenCloseOKAuthPopup(onError)(openOKAuthPopup(authURL));
      openOKAuthChanel(listenOKAuthChanel(onSuccess, onError));
    } catch (e) {
      console.log(e);
    }
  };

  /** закрытие канала обмена сообщениями */
  useEffect(() => closeOKAuthChanel, []);

  return {
    handleOKLogin,
  };
};
